import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import axios from '../api/axios';
import './ApplicationViewPublisher.css';

const IconDownload = () => (
  <svg width="13" height="13" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" style={{flexShrink:0}}>
    <path d="M7 1v8M4 6l3 3 3-3M2 11h10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const IconEye = () => (
  <svg width="13" height="13" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" style={{flexShrink:0}}>
    <path d="M1 7s2.5-4 6-4 6 4 6 4-2.5 4-6 4-6-4-6-4Z" stroke="currentColor" strokeWidth="1.5"/>
    <circle cx="7" cy="7" r="1.5" fill="currentColor"/>
  </svg>
);


export default function ApplicationViewPublisher({
  applicationId,
  onClose,
  locale = 'kk',
  embedded = true,
}) {
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dlProgress, setDlProgress] = useState({});
  const [preview, setPreview] = useState({
    open: false,
    loading: false,
    error: '',
    url: '',
    name: '',
    mime: '',
    kind: '',
    textContent: '',
    externalUrl: '',
    fileId: null,
  });

  const previewUrlRef = useRef(null);
  const fixedLocale = ['kk', 'kz'].includes(locale) ? 'kk' : 'ru';

  const t = useMemo(
    () =>
      ({
        kk: {
          applicationInfo: 'Өтінім',
          title: 'Атауы',
          subject: 'Пән',
          category: 'Санат',
          grade: 'Сынып',
          devLang: 'Әзірлеу тілі',
          teachLang: 'Оқу тілі',
          publisher: 'Баспа (шифр)',
          authors: 'Авторы',
          files: 'Файлдар',
          downloading: 'Жүктелуде',
          section: 'бөлім',
          download: 'Жүктеу',
          open: 'Ашу',
          close: 'Жабу',
          criteria_form: 'Өңдеу формасы',
          direction: 'Бағыт',
          publication_type: 'Басылым түрі',
          education_level: 'Білім деңгейі',
          format: 'Формат',
          service_type: 'Қызмет түрі',
          year: 'Жыл',
          status: 'Күйі',
          created_at: 'Жасалды',
          sent_at: 'Жіберілді',
          message: 'Хабарлама',
          public_status: 'Жария статус',
          internal_status: 'Ішкі статус',
          page_count: 'Бет саны',
          sheet_count: 'Парақ саны',
          section_count: 'Бөлім саны',
          archive: 'Архив',
          section_archive: 'Секция архиві',
          group: 'Топ',
          group_filled: 'Санаттардың толтырылуы',
          duration_minutes: 'Ұзақтығы (мин)',
          continuation_mode: 'Жалғастыру режимі',
          is_sent: 'Жіберілді ме',
          yes: 'Иә',
          no: 'Жоқ',
          file_no_link: 'Файл сілтемесі жоқ',
          preview_loading: 'Файл ашылуда...',
          preview_error: 'Файлды ашу мүмкін болмады',
          unsupported_preview: 'Бұл файл түрін браузер ішінде көрсету мүмкін емес.',
          open_external: 'Сыртқы сілтемені ашу',
          preview_title: 'Файлды қарау',
          link_blocked: 'Кейбір сыртқы сайттар iframe ішінде ашылмайды.',
        },
        ru: {
          applicationInfo: 'Заявка',
          title: 'Название',
          subject: 'Предмет',
          category: 'Категория',
          grade: 'Класс',
          devLang: 'Язык разработки',
          teachLang: 'Язык обучения',
          publisher: 'Издательство (шифр)',
          authors: 'Авторы',
          files: 'Файлы',
          section: 'раздел',
          downloading: 'Загрузка',
          download: 'Скачать',
          open: 'Открыть',
          close: 'Закрыть',
          criteria_form: 'Форма разработки',
          direction: 'Направление',
          publication_type: 'Тип издания',
          education_level: 'Уровень образования',
          format: 'Формат',
          service_type: 'Тип услуги',
          year: 'Год',
          status: 'Статус',
          created_at: 'Создано',
          sent_at: 'Отправлено',
          message: 'Сообщение',
          public_status: 'Публичный статус',
          internal_status: 'Внутренний статус',
          page_count: 'Страниц',
          sheet_count: 'Листов',
          section_count: 'Разделов',
          archive: 'Архив',
          section_archive: 'Архив секции',
          group: 'Группа',
          group_filled: 'Заполненность категорий',
          duration_minutes: 'Длительность (мин)',
          continuation_mode: 'Режим продолжения',
          is_sent: 'Отправлена',
          yes: 'Да',
          no: 'Нет',
          file_no_link: 'Нет ссылки на файл',
          preview_loading: 'Открываю файл...',
          preview_error: 'Не удалось открыть файл',
          unsupported_preview: 'Этот тип файла нельзя показать в браузере.',
          open_external: 'Открыть внешнюю ссылку',
          preview_title: 'Просмотр файла',
          link_blocked: 'Некоторые сайты не открываются внутри iframe.',
        },
      })[fixedLocale],
    [fixedLocale]
  );

  const revokePreviewUrl = useCallback((url) => {
    try { if (url) URL.revokeObjectURL(url); } catch {}
  }, []);

  const getAuthHeaders = useCallback(
    () => ({
      Authorization: `Bearer ${localStorage.getItem('token')}`,
      'Accept-Language': fixedLocale,
    }),
    [fixedLocale]
  );

  const setProgress = (key, value) => setDlProgress((prev) => ({ ...prev, [key]: value }));
  const clearProgress = (key) => setDlProgress((prev) => { const c = { ...prev }; delete c[key]; return c; });
  const progressState = (key) => {
    const val = dlProgress[key];
    const hasValue = Number.isFinite(val);
    return { isLoading: hasValue && val < 100, hasPct: hasValue && val > 0, pct: hasValue ? val : 0 };
  };

  const normalizeApplication = (app) => {
    if (!app || typeof app !== 'object') return null;
    return {
      ...app,
      files: Array.isArray(app.files) ? app.files : [],
      categories: Array.isArray(app.categories) ? app.categories : [],
      group: app.group && typeof app.group === 'object' ? app.group : app.group ?? null,
      archive: app.archive && typeof app.archive === 'object' ? app.archive : app.archive ?? null,
      internal_status: app.internal_status && typeof app.internal_status === 'object' ? app.internal_status : app.internal_status ?? null,
      public_status: app.public_status && typeof app.public_status === 'object' ? app.public_status : app.public_status ?? null,
    };
  };

  const pickLocale = (val) => {
    if (val == null) return '—';
    if (typeof val === 'string' || typeof val === 'number') return String(val);
    if (typeof val === 'object') {
      const kzLike = val.kk ?? val.kz ?? val.name_kz ?? val.title_kz ?? val.label_kz ?? val.value_kz ?? val.status_kz;
      const ruLike = val.ru ?? val.name_ru ?? val.title_ru ?? val.label_ru ?? val.value_ru ?? val.status_ru;
      const primary = fixedLocale === 'kk' ? kzLike ?? ruLike : ruLike ?? kzLike;
      if (typeof primary === 'string' || typeof primary === 'number') return String(primary);
      if (typeof val.name === 'string') return String(val.name);
      if (typeof val.status === 'string') return String(val.status);
      if (typeof val.title === 'string') return String(val.title);
      for (const v of Object.values(val)) {
        if (typeof v === 'string' || typeof v === 'number') return String(v);
      }
      return '—';
    }
    return String(val);
  };

  const pickField = (...vals) => {
    for (const v of vals) {
      if (v == null) continue;
      if (typeof v === 'string' && v.trim() === '') continue;
      return v;
    }
    return null;
  };

  const formatDate = (s) => {
    if (!s) return '—';
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return String(s);
    return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;
  };

  const sanitizeFilename = (name) => {
    if (!name) return null;
    return String(name).replace(/[<>:"/\\|?*\x00-\x1F]/g, '_').trim() || null;
  };

  const getFileExtension = (name = '') => {
    const clean = String(name).split('?')[0].split('#')[0];
    const idx = clean.lastIndexOf('.');
    return idx >= 0 ? clean.slice(idx + 1).toLowerCase() : '';
  };

  const parseFilenameFromHeaders = (headers = {}) => {
    const xFileName = headers['x-file-name'];
    if (xFileName) { try { return decodeURIComponent(xFileName); } catch { return xFileName; } }
    const cd = headers['content-disposition'] || '';
    const utfMatch = cd.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
    if (utfMatch?.[1]) { try { return decodeURIComponent(utfMatch[1]); } catch { return utfMatch[1]; } }
    const normalMatch = cd.match(/filename\s*=\s*"([^"]+)"/i) || cd.match(/filename\s*=\s*([^;]+)/i);
    if (normalMatch?.[1]) return normalMatch[1].trim();
    return null;
  };

  const detectPreviewKind = (mime = '', name = '') => {
    const m = String(mime || '').toLowerCase();
    const ext = getFileExtension(name);
    if (m.includes('pdf') || ext === 'pdf') return 'pdf';
    if (m.startsWith('image/') || ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg'].includes(ext)) return 'image';
    if (m.startsWith('text/') || ['txt', 'log', 'json', 'xml', 'csv', 'md', 'js', 'jsx', 'ts', 'tsx', 'css', 'html', 'php'].includes(ext)) return 'text';
    if (m.startsWith('video/')) return 'video';
    if (m.startsWith('audio/')) return 'audio';
    return 'unsupported';
  };

  const closePreview = useCallback(() => {
    revokePreviewUrl(previewUrlRef.current);
    previewUrlRef.current = null;
    setPreview({ open: false, loading: false, error: '', url: '', name: '', mime: '', kind: '', textContent: '', externalUrl: '', fileId: null });
  }, [revokePreviewUrl]);

  useEffect(() => () => revokePreviewUrl(previewUrlRef.current), [revokePreviewUrl]);

  const downloadViaPresigned = async (endpoint, suggestedName = 'file', progressKey) => {
    try {
      setProgress(progressKey, 1);
      const res = await axios.get(endpoint, { headers: getAuthHeaders() });
      const presignedUrl = res.data?.url;
      if (!presignedUrl) throw new Error('No presigned URL in response');
      // Открываем presigned URL напрямую — браузер сам решит скачать или показать
      window.open(presignedUrl, '_blank', 'noopener,noreferrer');
      setProgress(progressKey, 100);
      setTimeout(() => clearProgress(progressKey), 500);
    } catch (error) {

      alert(fixedLocale === 'kk' ? '❌ Файлды жүктеу мүмкін болмады' : '❌ Не удалось скачать файл');
      clearProgress(progressKey);
    }
  };

  const downloadFileById = (fileId, suggestedName, progressKey) =>
    downloadViaPresigned(`/publisher/files/${fileId}`, suggestedName, progressKey);

  const downloadSectionArchiveById = (fileId, suggestedName, progressKey) =>
    downloadViaPresigned(`/publisher/files/${fileId}/section-archive`, suggestedName, progressKey);

  const downloadArchive = async (appId, suggestedName, progressKey) => {
    if (!appId) { alert(fixedLocale === 'kk' ? '❌ Өтінім ID жоқ' : '❌ Нет ID заявки'); return; }
    try {
      setProgress(progressKey, 0);
      const res = await axios.get(`/publisher/applications/${appId}/download-archive`, { headers: getAuthHeaders() });
      const presignedUrl = res.data?.url;
      if (!presignedUrl) throw new Error('No presigned URL in response');
      window.open(presignedUrl, '_blank', 'noopener,noreferrer');
      setProgress(progressKey, 100);
      setTimeout(() => clearProgress(progressKey), 500);
    } catch (e) {
 
      alert(fixedLocale === 'kk' ? '❌ Файлды жүктеу мүмкін болмады' : '❌ Не удалось скачать файл');
      clearProgress(progressKey);
    }
  };

  const openFilePreview = useCallback(
    async (file, displayName) => {
      const linkUrl = file?.link_url || file?.file_link || file?.url || null;
      const isLink = Boolean(file?.is_link || file?.link_url || file?.file_link);

if (isLink && linkUrl) {
  window.open(linkUrl, '_blank', 'noopener,noreferrer');
  return;
}

      if (!file?.id) { alert(t.file_no_link); return; }

      closePreview();
      setPreview({ open: true, loading: true, error: '', url: '', name: displayName, mime: '', kind: '', textContent: '', externalUrl: '', fileId: file.id });

      try {
        const res = await axios.get(`/publisher/files/${file.id}/preview`, { headers: getAuthHeaders(), responseType: 'blob' });
        const fileName = parseFilenameFromHeaders(res.headers) || sanitizeFilename(displayName) || `file_${file.id}`;
        const mime = res.headers['content-type'] || res.data?.type || 'application/octet-stream';
        const blob = new Blob([res.data], { type: mime });
        const objectUrl = URL.createObjectURL(blob);
        previewUrlRef.current = objectUrl;
        const kind = detectPreviewKind(mime, fileName);
        let textContent = '';
        if (kind === 'text') textContent = await blob.text();
        setPreview({ open: true, loading: false, error: '', url: objectUrl, name: fileName, mime, kind, textContent, externalUrl: '', fileId: file.id });
      } catch (error) {
        
        setPreview({ open: true, loading: false, error: error?.response?.data?.message || t.preview_error, url: '', name: displayName, mime: '', kind: '', textContent: '', externalUrl: '', fileId: file?.id ?? null });
      }
    },
    [closePreview, getAuthHeaders, t.file_no_link, t.preview_error]
  );

  useEffect(() => {
    if (!applicationId) return;
    const ctrl = new AbortController();
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`/publisher/applications/${applicationId}`, { headers: getAuthHeaders(), signal: ctrl.signal });
        setApplication(normalizeApplication(res.data?.application ?? res.data));
      } catch (err) {
        if (err?.name !== 'CanceledError' && err?.code !== 'ERR_CANCELED') {

          alert(fixedLocale === 'kk' ? 'Өтінішті ашу қатесі!' : 'Ошибка открытия заявки!');
          onClose?.();
        }
      } finally {
        setLoading(false);
      }
    })();
    return () => ctrl.abort();
  }, [applicationId, fixedLocale, getAuthHeaders, onClose]);

  useEffect(() => {
    if (embedded) return;
    const onEsc = (e) => {
      if (e.key === 'Escape') {
        if (preview.open) closePreview();
        else onClose?.();
      }
    };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [embedded, onClose, preview.open, closePreview]);

  if (loading) return <div className="avh-state">{fixedLocale === 'kk' ? 'Жүктелуде...' : 'Загрузка...'}</div>;
  if (!application) return <div className="avh-state avh-state--empty">{fixedLocale === 'kk' ? 'Өтініш табылмады.' : 'Заявка не найдена.'}</div>;

  const g = application?.group && typeof application.group === 'object' ? application.group : {};
  const gm = g?.meta && typeof g.meta === 'object' ? g.meta : {};

  const safeMessage = !application.message || application.message === 'null' || application.message === 'None'
    ? '—' : String(application.message);

  const archiveObj = application?.archive && typeof application.archive === 'object' ? application.archive : null;
  const hasArchive = Boolean(archiveObj && (archiveObj.id || archiveObj.original_name || archiveObj.url)) || Boolean(application?.archive_path || application?.id);
  const archiveName = archiveObj?.original_name || archiveObj?.name || application?.archive_original_name || `archive_${application?.id || 'file'}.zip`;
  const archivePS = progressState('archive');

  const rows = [
    [t.title, pickField(application.title, g.title) || '—'],
    [t.category, pickLocale(pickField(application.category, (application.categories || [])[0]))],
    [t.criteria_form, pickLocale(pickField(application.criteria_form, gm.criteria_form, g.criteria_form))],
    [t.status, pickLocale(pickField(application.public_status, application.status, g.public_status, g.status))],
    [t.created_at, formatDate(pickField(application.created_at))],
    [t.sent_at, formatDate(pickField(application.sent_at, g.sent_at))],
    [t.authors, pickField(application.authors) ?? '—'],
    [t.duration_minutes, application.duration_minutes ?? '—'],
    [t.page_count, application.page_count ?? '—'],
    [t.sheet_count, application.sheet_count ?? '—'],
    [t.section_count, application.section_count ?? '—'],
    [t.continuation_mode, application.continuation_mode ?? '—'],
    [t.is_sent, application.is_sent ? t.yes : t.no],
    [t.message, safeMessage],
  ].filter(([, v]) => v && v !== '—');

  const renderPreviewBody = () => {
    if (preview.loading) return <div className="avh-preview-center"><span className="avh-preview-hint">{t.preview_loading}</span></div>;
    if (preview.error) return <div className="avh-preview-center"><span className="avh-preview-error">{preview.error}</span></div>;
    if (preview.kind === 'pdf') return <iframe src={preview.url} title={preview.name} className="avh-preview-frame" />;
    if (preview.kind === 'image') return <div className="avh-preview-image-wrap"><img src={preview.url} alt={preview.name} className="avh-preview-image" /></div>;
    if (preview.kind === 'text') return <pre className="avh-preview-text">{preview.textContent}</pre>;
    if (preview.kind === 'video') return <div className="avh-preview-center"><video src={preview.url} controls className="avh-preview-media" /></div>;
    if (preview.kind === 'audio') return <div className="avh-preview-center"><audio src={preview.url} controls style={{ width: '100%' }} /></div>;
    if (preview.kind === 'external') return (
      <div className="avh-preview-external">
        <p className="avh-preview-hint">{t.link_blocked}</p>
        <iframe src={preview.externalUrl} title={preview.name} className="avh-preview-frame" style={{ flex: 1 }} />
        <div className="avh-preview-external-footer">
          <a href={preview.externalUrl} target="_blank" rel="noopener noreferrer" className="avh-link-btn">↗ {t.open_external}</a>
        </div>
      </div>
    );
    return <div className="avh-preview-center"><span className="avh-preview-hint">{t.unsupported_preview}</span></div>;
  };

  return (
    <div className={`avh ${embedded ? 'avh--embedded' : ''}`} {...(!embedded ? { role: 'dialog', 'aria-modal': 'true' } : {})}>

      {!embedded && (
        <button type="button" className="avh-close-x" aria-label={t.close} onClick={onClose}>×</button>
      )}

      {!embedded && (
        <div className="avh-header">
          <h2 className="avh-heading">{t.applicationInfo}</h2>
          {application.id && <span className="avh-id">#{application.id}</span>}
        </div>
      )}

      <dl className="avh-dl">
        {rows.map(([label, value], i) => (
          <div key={`${label}-${i}`} className="avh-dl__row">
            <dt className="avh-dl__label">{label}</dt>
            <dd className="avh-dl__value">{value}</dd>
          </div>
        ))}

        {hasArchive && (
          <div className="avh-dl__row">
            <dt className="avh-dl__label">{t.archive}</dt>
            <dd className="avh-dl__value">
              <button
                className={`avh-action-btn ${archivePS.isLoading ? 'avh-action-btn--loading' : ''}`}
                type="button"
                disabled={archivePS.isLoading}
                onClick={() => downloadArchive(application.id, archiveName, 'archive')}
              >
                {archivePS.isLoading
                  ? <><span className="avh-spinner" />{t.downloading}{archivePS.hasPct ? ` ${archivePS.pct}%` : '...'}</>
                  : <><IconDownload /> {t.download} — {archiveName}</>
                  }
              </button>
              {archivePS.isLoading && <div className="avh-progress"><div className={`avh-progress__bar ${archivePS.hasPct ? '' : 'is-indeterminate'}`} style={archivePS.hasPct ? { width: `${archivePS.pct}%` } : undefined} /></div>}
            </dd>
          </div>
        )}
      </dl>

      {Array.isArray(application.files) && application.files.length > 0 && (
        <div className="avh-files">
          <h3 className="avh-files__heading">{t.files}</h3>
          <ul className="avh-files__list">
            {application.files.map((file, index) => {
              const sectionNum = file.section ?? index + 1;
              const sectionLabel = `${sectionNum} ${t.section}`;
              const displayName = (typeof file.name === 'string' && file.name.trim()) || (typeof file.application_file_name === 'string' && file.application_file_name.trim()) || sectionLabel;
              const isLink = Boolean(file.is_link || file.link_url || file.file_link);
              const dlUrl = file.download_url || file.url;
              const key = file.id ? `id:${file.id}` : `idx:${index}`;
              const ps = progressState(key);
              const suggestedName = sanitizeFilename(displayName) || `file_${sectionNum}`;
const hasSectionArchive = Boolean(file.has_section_archive || file.section_archive_original_name || file.section_archive_name || file.section_archive_download_url);
              const sectionArchiveKey = file.id ? `section-archive:${file.id}` : `section-archive:${index}`;
              const sectionArchivePS = progressState(sectionArchiveKey);
              const sectionArchiveName = file.section_archive_original_name || file.section_archive_name || `${sanitizeFilename(displayName) || `section_${sectionNum}`}.zip`;

              return (
                <li key={file.id || index} className="avh-file">
                  <div className="avh-file__info">
                    <span className={`avh-file__badge ${isLink ? 'avh-file__badge--link' : ''}`}>{isLink ? 'LINK' : 'FILE'}</span>
                    <span className="avh-file__name" title={displayName}>{displayName}</span>
                  </div>
                  <div className="avh-file__actions">
                    <button type="button" className="avh-action-btn avh-action-btn--ghost" onClick={() => openFilePreview(file, displayName)}>
                      <IconEye /> {t.open}
                    </button>

                    {!isLink && (
                      <div className="avh-dl-wrap">
                        <button
                          type="button"
                          className={`avh-action-btn ${ps.isLoading ? 'avh-action-btn--loading' : ''}`}
                          disabled={ps.isLoading || (!file.id && !dlUrl)}
                          onClick={() => {
                            if (file.id) { downloadFileById(file.id, suggestedName, key); return; }
                            if (dlUrl) { downloadViaPresigned(dlUrl, suggestedName, key); return; }
                            alert(t.file_no_link);
                          }}
                        >
                          {ps.isLoading ? <><span className="avh-spinner" />{t.downloading}{ps.hasPct ? ` ${ps.pct}%` : '...'}</> : <><IconDownload /> {t.download}</>
                        }
                        </button>
                        {ps.isLoading && <div className="avh-progress"><div className={`avh-progress__bar ${ps.hasPct ? '' : 'is-indeterminate'}`} style={ps.hasPct ? { width: `${ps.pct}%` } : undefined} /></div>}
                      </div>
                    )}

                    {hasSectionArchive && (
                      <div className="avh-dl-wrap">
                        <button
                          type="button"
                          className={`avh-action-btn ${sectionArchivePS.isLoading ? 'avh-action-btn--loading' : ''}`}
                          disabled={sectionArchivePS.isLoading && !file.id && !file.section_archive_download_url}
                          onClick={() => {
                            if (file.id) { downloadSectionArchiveById(file.id, sectionArchiveName, sectionArchiveKey); return; }
                            if (file.section_archive_download_url) { downloadViaPresigned(file.section_archive_download_url, sectionArchiveName, sectionArchiveKey); }
                          }}
                        >
                          {sectionArchivePS.isLoading ? <><span className="avh-spinner" />{t.downloading}{sectionArchivePS.hasPct ? ` ${sectionArchivePS.pct}%` : '...'}</> : <><IconDownload /> {t.section_archive}</>
                        }
                        </button>
                        {sectionArchivePS.isLoading && <div className="avh-progress"><div className={`avh-progress__bar ${sectionArchivePS.hasPct ? '' : 'is-indeterminate'}`} style={sectionArchivePS.hasPct ? { width: `${sectionArchivePS.pct}%` } : undefined} /></div>}
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {!embedded && (
        <div className="avh-footer">
          <button className="avh-action-btn avh-action-btn--ghost" onClick={onClose}>{t.close}</button>
        </div>
      )}

      {preview.open && ReactDOM.createPortal(
        <div className="avh-overlay" onClick={closePreview}>
          <div className="avh-modal" onClick={(e) => e.stopPropagation()}>
            <div className="avh-modal__header">
              <div className="avh-modal__meta">
                <span className="avh-modal__title">{t.preview_title}</span>
                <span className="avh-modal__name" title={preview.name}>{preview.name || '—'}</span>
              </div>
              <div className="avh-modal__actions">
                {preview.fileId && (
                  <button type="button" className="avh-action-btn"
                    onClick={() => downloadFileById(preview.fileId, sanitizeFilename(preview.name) || `file_${preview.fileId}`, `preview-dl-${preview.fileId}`)}>
                    ↓ {t.download}
                  </button>
                )}
                <button type="button" className="avh-action-btn avh-action-btn--ghost" onClick={closePreview}>{t.close}</button>
              </div>
            </div>
            <div className="avh-modal__body">{renderPreviewBody()}</div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}